/* ── ECH PROOFING, INSIDE THE STUDIO (Milestone 2, 2026-10-06) ─────────────────────
   Jack's rule for this feature: it is NOT a new screen. It is the Design Email screen
   people already know — same navy canvas, same 600px paper, same top bar, same white
   inspector — with comments on it. The design is Jack's Oct 5 mockup
   (Projects/Email/Smart Email Request/_mockups/proofing-workflow-20261005/ech-proofing.html,
   "Review screen"), whose markup and class names these parts reproduce:
     · the navy job strip above the bar (job · round dot · status pill)
     · the bar: "Review Email" / "Edit Email", Desktop|Mobile, zoom, round button, … More, gold action
     · a note + round legend floating left of the paper (.cvside)
     · pins at the exact spot, coloured by round, a highlight box on the focused block
     · the inspector: Comments (division) · Division comments / Fields|Comments per block (editor)
   The rules are the server's (emailbuilder-be proof-model `check()`); these parts only show
   what `can` allows and print the server's reason when it refuses. */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import type { ReactNode, RefObject } from "react";
import Combo from "../../components/Combo";
import { blockLabel } from "../../email/blocks";
import type { AnyBlockInstance } from "../../email/document";
import { getProofJob, getProofVersionDoc, getProofVersionHtml } from "../../services/api";
import type { ProofComment, ProofHighlight, ProofJob, ProofOp, ProofState, ProofView as ProofData } from "../../services/api";
import "./proof-studio.css";

export interface ProofCtx {
  id: string;
  mode: "comment" | "edit";
  data: ProofData;
  /** a non-editor opened the Editor link and was sent to the comment view */
  rerouted?: boolean;
  busy: boolean;
  saving: boolean;
  dirty: boolean;
  /** one door for every write; resolves to the server's error text, or "" when it saved */
  act: (op: ProofOp, body: Record<string, unknown>) => Promise<string>;
  onSaveRevision: () => Promise<boolean> | void;
  /* what Digital Ops highlights for the division, saved with the next revision (10-26 call) */
  hl: ProofHighlight[];
  setHl: (h: ProofHighlight[]) => void;
  reviewLink: string;
  editorLink: string;
}

export const PROOF_TAGS = ["Copy", "Image", "Homesites", "Button / link", "Date / offer", "Disclaimer", "Other"];
/* round colours, from the mockup (RC) — round 0 = Digital Ops' notes before round 1 */
const RC = ["#3f9a9a", "#C8A664", "#7d63c9", "#d0643f", "#2f6fb5", "#3d8b5a"];
export const roundColor = (n: number) => (n <= 0 ? "#9fb0cc" : RC[(n - 1) % RC.length]);
const when = (iso: string) => {
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
};

/* ── the anchor names its block three ways, so a pin survives a rebuild that re-mints
   block ids: "<index>:<type>:<id>" → match the id, else the same type at the same
   position, else the same position. */
export const blockKey = (blocks: AnyBlockInstance[], id: string) => {
  const i = blocks.findIndex((b) => b.id === id);
  return i < 0 ? "" : i + ":" + blocks[i].type + ":" + id;
};
export function resolveBlock(blocks: AnyBlockInstance[], key?: string): AnyBlockInstance | null {
  if (!key) return null;
  const [ix, type, ...rest] = key.split(":");
  const id = rest.join(":");
  return blocks.find((b) => b.id === id)
    || (blocks[+ix] && blocks[+ix].type === type ? blocks[+ix] : null)
    || blocks.find((b) => b.type === type) || null;
}
const blockName = (blocks: AnyBlockInstance[], key?: string) => {
  const b = resolveBlock(blocks, key);
  return b ? blockLabel(b.type) : "Email";
};

export interface ProofUi {
  draft: { block: string; x: number; y: number } | null;
  setDraft: (d: ProofUi["draft"]) => void;
  focus: string | null;
  setFocus: (id: string | null) => void;
  rf: "all" | number;
  setRf: (r: "all" | number) => void;
  panel: "versions" | null;
  setPanel: (p: "versions" | null) => void;
  itab: "fields" | "comments";
  setItab: (t: "fields" | "comments") => void;
  notice: string;
  setNotice: (s: string) => void;
  err: string;
  setErr: (s: string) => void;
  /* edit view: "+ Add comment" armed — the next click on the email drops a pin instead of selecting */
  adding: boolean;
  setAdding: (b: boolean) => void;
  /* M2 Discovery (2026-10-12): the left panel's Comments | Versions tab, one saved version
     open read-only on the canvas, or two side by side */
  ltab: "comments" | "versions";
  setLtab: (t: "comments" | "versions") => void;
  view: number | null;
  setView: (n: number | null) => void;
  cmp: [number, number] | null;
  setCmp: (c: [number, number] | null) => void;
  /* edit view: "Highlight a block" armed — the next click on a block highlights it */
  hlPick: boolean;
  setHlPick: (b: boolean) => void;
  /* edit view: "Highlight text" armed — blocks stop dragging so a drag selects words */
  hlText: boolean;
  setHlText: (b: boolean) => void;
  /* review view: highlights + "what changed" on the email (on by default) */
  showChanges: boolean;
  setShowChanges: (b: boolean) => void;
}
export function useProofUi(): ProofUi {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<ProofUi["draft"]>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [rf, setRf] = useState<"all" | number>("all");
  const [panel, setPanel] = useState<"versions" | null>(null);
  const [itab, setItab] = useState<"fields" | "comments">("fields");
  const [notice, setNotice] = useState("");
  const [err, setErr] = useState("");
  const [ltab, setLtab] = useState<"comments" | "versions">("comments");
  const [view, setView] = useState<number | null>(null);
  const [cmp, setCmp] = useState<[number, number] | null>(null);
  const [hlPick, setHlPick] = useState(false);
  const [hlText, setHlText] = useState(false);
  const [showChanges, setShowChanges] = useState(true);
  return { draft, setDraft, focus, setFocus, rf, setRf, panel, setPanel, itab, setItab, notice, setNotice, err, setErr, adding, setAdding, ltab, setLtab, view, setView, cmp, setCmp, hlPick, setHlPick, hlText, setHlText, showChanges, setShowChanges };
}

/* ── the navy job strip (mockup .strip) ── */
/* ── the job's send date, subject and preheader, read-only (M2 Discovery, 2026-10-12) ──
   "Sometimes we send it back and forth and we're already well past their target deployment
   date." Send date first and gold; subject and preheader after it. Read from the job's
   Airtable row by the backend (proof-job) and changed in Airtable as today — nothing is
   written back from here. A job with no row, or a field left blank, shows nothing for it. */
const sendWhen = (iso: string) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};
export function useProofJob(id: string): ProofJob | null {
  const [job, setJob] = useState<ProofJob | null>(null);
  useEffect(() => {
    let live = true;
    getProofJob(id).then((j) => { if (live) setJob(j); });
    return () => { live = false; };
  }, [id]);
  return job;
}
function JobChips({ job }: { job: ProofJob | null }) {
  if (!job) return null;
  const past = job.sendAt && new Date(job.sendAt).getTime() < Date.now();
  const tip = "From the job's Airtable row" + (job.name ? " (" + job.name + ")" : "") + ". Change it in Airtable.";
  return (
    <>
      {job.sendAt && <span className={"tag pjob pdate" + (past ? " past" : "")} title={tip}><span className="k">Send date</span>{sendWhen(job.sendAt)}{past ? " · passed" : ""}</span>}
      {job.subject && <span className="tag pjob" title={tip}><span className="k">Subject</span><span className="v">{job.subject}</span></span>}
      {job.preheader && <span className="tag pjob" title={tip}><span className="k">Preheader</span><span className="v">{job.preheader}</span></span>}
    </>
  );
}

export function ProofStrip({ p, division, title }: { p: ProofCtx; division?: string; title?: string }) {
  const s = p.data.state!;
  const job = useProofJob(p.id);
  /* one status, not two (Jack, 2026-10-07): "Round 2 Open" says it — the pill only shows when there's no round line or the job is approved */
  const st = s.approved ? { t: "Approved", c: "lock" } : s.round === 0 ? { t: "With Digital Ops", c: "lock" } : null;
  return (
    <div className="pstrip">
      <span className="tag">Email {p.id}</span>
      {title && <b>{title}</b>}
      {division && <span className="dim">{division}</span>}
      {st && <span className={"pill " + st.c}>{st.t}</span>}
      {s.round > 0 && !s.approved && <span className="rdot" style={{ ["--c" as string]: roundColor(s.round) }}>Round {s.round} {s.locked ? "Closed" : "Open"}</span>}
      <JobChips job={job} />
      <span className="sp" />
      {p.data.me && <span className="who">{p.data.me.name} · {p.data.me.role === "reviewer" ? "Reviewer" : p.data.me.role === "admin" ? "Admin" : "Editor"}</span>}
    </div>
  );
}

/* ── the bar's right-hand group (replaces Done) ── */
export function ProofBarRight({ p, ui, onCopy, blocks }: { p: ProofCtx; ui: ProofUi; onCopy: (text: string, msg: string) => void; blocks: AnyBlockInstance[] }) {
  const s = p.data.state!, can = p.data.can!;
  const [menu, setMenu] = useState(false);
  /* Katy, 10-26 call: Digital Ops picks the round so it matches the Airtable job. The default is
     still the next round; ▾ offers "reopen" (a division that sent a round back early) and lower
     numbers. "Change round number…" corrects the round that is already open. */
  const [pickOpen, setPickOpen] = useState<"open" | "relabel" | null>(null);
  const [nextPick, setNextPick] = useState<number | null>(null);
  useEffect(() => { setNextPick(null); }, [s.round, s.locked]);
  const nextN = nextPick ?? s.round + 1;
  useEffect(() => {
    if (!ui.hlPick && !ui.hlText) return;
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { ui.setHlPick(false); ui.setHlText(false); } };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [ui]);
  const openRound = async (n: number) => {
    ui.setErr("");
    /* a revision with unsaved edits or highlights is saved first, so the round shows it */
    if (p.dirty) { const ok = await p.onSaveRevision(); if (ok === false) return; }
    const e = await p.act("unlock", n === s.round + 1 ? {} : { round: n });
    if (e) ui.setErr(e); // no "Round N is open" card (Jack, 10-26) — the strip already says it
  };
  const relabel = async (n: number) => {
    ui.setErr("");
    const e = await p.act("unlock", { round: n, relabel: true });
    if (e) ui.setErr(e);
  };
  const [confirm, setConfirm] = useState<"changes" | "approved" | "approve" | null>(null);
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menu]);
  const go = async (op: ProofOp, after?: () => void) => {
    ui.setErr("");
    const e = await p.act(op, {});
    if (e) ui.setErr(e); else after?.();
  };
  const latest = s.versions.length ? s.versions[s.versions.length - 1].n : 0;
  const openN = s.comments.filter((c) => c.state === "open" && c.round === s.round).length;
  return (
    <>
      <div className="esmorewrap">
        <button className="estb" onClick={(e) => { e.stopPropagation(); setMenu(!menu); }}>… More</button>
        <div className={"esovm" + (menu ? " on" : "")} onClick={(e) => e.stopPropagation()}>
          <button className="esovi" onClick={() => { setMenu(false); ui.setLtab("versions"); }}>Version history</button>
          <button className="esovi" onClick={() => { setMenu(false); onCopy(p.reviewLink, "Division review link copied"); }}>Copy division review link</button>
          {p.mode === "edit" && <button className="esovi" onClick={() => { setMenu(false); onCopy(p.editorLink, "Editor link copied"); }}>Copy editor link</button>}
          {p.mode === "edit" && can.setround && <button className="esovi" onClick={() => { setMenu(false); setPickOpen("relabel"); }}>Change round number…</button>}
          {/* no "Mark approved" — approval is a job status in Airtable (Jack, 2026-10-07) */}
        </div>
      </div>
      {p.mode === "edit" ? (
        <>
          {can.save && (
            <>
              {/* words: the first click arms it (blocks stop dragging so a drag selects text),
                  the click after a selection highlights it */}
              <button className={"estb phlb" + (ui.hlText ? " on" : "")} title="Select words in the email, then click here" onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  ui.setHlPick(false); ui.setErr("");
                  const e = addTextHighlight(p, blocks);
                  if (!e) { ui.setHlText(false); return; }
                  ui.setHlText(!ui.hlText);
                }}><i />{ui.hlText ? "Select words, then click here" : "Highlight text"}</button>
              <button className={"estb phlb" + (ui.hlPick ? " on" : "")} onClick={() => { ui.setAdding(false); ui.setHlText(false); ui.setHlPick(!ui.hlPick); }}><i />{ui.hlPick ? "Click a block…" : "Highlight a block"}</button>
            </>
          )}
          {can.unlock && (
            <span className="rsplit">
              <button className="estb" disabled={p.busy || p.saving} onClick={() => openRound(nextN)}>Open round {nextN}</button>
              <button className="estb car" aria-label="Pick the round" aria-haspopup="listbox" disabled={p.busy} onClick={(e) => { e.stopPropagation(); setPickOpen(pickOpen === "open" ? null : "open"); }}>▾</button>
              {pickOpen === "open" && <RoundPick title="Open which round?" foot="Pick the round the job shows in Airtable."
                rows={roundRows(s, "open")} on={nextN} onPick={(n) => { setNextPick(n); setPickOpen(null); }} onClose={() => setPickOpen(null)} />}
            </span>
          )}
          {can.lock && <button className="estb" disabled={p.busy} onClick={() => go("lock")}>Close round {s.round}</button>}
          {pickOpen === "relabel" && (
            <span className="rsplit"><RoundPick title={"Round " + s.round + " should be…"} foot="The round and its comments take the new number."
              rows={roundRows(s, "relabel")} on={null} onPick={(n) => { setPickOpen(null); relabel(n); }} onClose={() => setPickOpen(null)} /></span>
          )}
          <button className="esdone" disabled={!can.save || !p.dirty || p.saving} onClick={() => { void p.onSaveRevision(); }}>{p.saving ? "Saving…" : "Save revision"}</button>
        </>
      ) : (
        /* the division's one action (Jack, 2026-10-07: Approve removed — sign-off happens outside the app) */
        <button className="estb" disabled={!can.submit || !openN || p.busy} onClick={() => setConfirm("changes")}>Request changes{openN && can.submit ? " (" + openN + ")" : ""}</button>
      )}
      {confirm && (
        <div className="pconfirm" role="alertdialog" aria-label="Confirm" onClick={(e) => e.stopPropagation()}>
          <span>{confirm === "changes"
            ? "Send round " + s.round + " back with " + openN + " change" + (openN === 1 ? "" : "s") + "? Comments close until Digital Ops sends it back."
            : confirm === "approved"
              ? "Approve " + vName(s, latest).toLowerCase() + "? It goes on to Legal for final sign-off."
              : "Mark this email approved? Nobody can comment or edit after this."}</span>
          <button className="esdone" disabled={p.busy} onClick={async () => {
            ui.setErr("");
            const e = confirm === "approve" ? await p.act("approve", {}) : await p.act("submit", { decision: confirm });
            if (e) { ui.setErr(e); return; }
            if (confirm !== "approve") ui.setNotice((confirm === "approved" ? "Approved. " : "Changes requested. ") + "Change the job status in Airtable so Digital Ops knows.");
            setConfirm(null);
          }}>{confirm === "changes" ? "Request changes" : confirm === "approved" ? "Approve" : "Mark approved"}</button>
          <button className="estb" onClick={() => setConfirm(null)}>Cancel</button>
        </div>
      )}
    </>
  );
}

/* ── note + round legend, floating left of the paper (mockup .cvside) ── */
export function ProofSide({ p, ui }: { p: ProofCtx; ui: ProofUi }) {
  const s = p.data.state!;
  const [rrSeen, setRrSeen] = useState(false);
  const latest = s.versions.length ? s.versions[s.versions.length - 1].n : null;
  const note = (() => {
    if (s.approved) return { c: "lock", b: "Approved.", t: " Division and Legal approved this email. The Email Specialist schedules it." };
    if (p.mode === "comment") {
      /* open round: no card (Jack, 2026-10-07: the strip already says it's open) */
      if (!s.locked) return null;
      if (s.round === 0) return { c: "", b: "Digital Ops is reviewing this email first.", t: " You'll get the link when round 1 opens." };
      if (s.lockKind === "reviewer" && s.decision && s.decision.value === "approved") return { c: "lock", b: "You approved this email.", t: " Legal gives final sign-off next." };
      if (s.lockKind === "reviewer") return { c: "", b: "You requested changes on round " + s.round + ".", t: " Comments are closed until Digital Ops sends it back." };
      return { c: "", b: "Comments are closed.", t: " Digital Ops is working on round " + s.round + ". You'll get the link when the next round opens." };
    }
    /* edit view: no status card (Jack, 2026-10-07: same as the review view — the strip and the
       Open/Close round button already say where the round is) */
    return null;
  })();
  void latest;
  /* COMPACT (Jack, 2026-10-06: the floating note + rounds box beside the paper looked odd and
     took room). The status is one short card at the top of the comments panel; rounds are the
     R1/R2/All chips already in that panel; reminders and errors sit under it. */
  /* Lauren on the 10-06 call: a non-editor on the Editor link should be told why (Jack's wording, 2026-10-07) */
  const rr = !!p.rerouted && p.mode === "comment" && !rrSeen;
  if (!note && !rr && !ui.notice && !ui.err) return null;
  return (
    <div className="pside">
      {rr && <div className="pnote lock" role="status">You've been routed to the comment version because you don't have editor access. <button className="backl" onClick={() => setRrSeen(true)}>Got it</button></div>}
      {note && <div className={"pnote" + (note.c ? " " + note.c : "")}><b>{note.b}</b>{note.t}</div>}
      {ui.notice && <div className="pnote open">{ui.notice} <button className="backl" onClick={() => ui.setNotice("")}>Got it</button></div>}
      {ui.err && <div className="pnote bad" role="alert">{ui.err} <button className="backl" onClick={() => ui.setErr("")}>OK</button></div>}
    </div>
  );
}

/* ── pins over the paper: measured from the rendered blocks, so they move with zoom,
   device and anything that changes a block's height ── */
export function ProofPins({ p, ui, blocks, paperRef, zoom }: { p: ProofCtx; ui: ProofUi; blocks: AnyBlockInstance[]; paperRef: RefObject<HTMLDivElement | null>; zoom: number }) {
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    const el = paperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(bump);
    ro.observe(el);
    el.querySelectorAll("img").forEach((im) => im.addEventListener("load", bump));
    return () => ro.disconnect();
  }, [paperRef, bump, blocks]);
  const [box, setBox] = useState<Record<string, { x: number; y: number; w: number; h: number }>>({});
  useLayoutEffect(() => {
    const root = paperRef.current;
    if (!root) return;
    const r0 = root.getBoundingClientRect();
    const z = zoom || 1;
    const out: Record<string, { x: number; y: number; w: number; h: number }> = {};
    root.querySelectorAll<HTMLElement>(".esmod[data-id]").forEach((m) => {
      const r = m.getBoundingClientRect();
      out[m.dataset.id as string] = { x: (r.left - r0.left) / z, y: (r.top - r0.top) / z, w: r.width / z, h: r.height / z };
    });
    setBox(out);
  }, [tick, blocks, zoom, paperRef]);
  const s = p.data.state!;
  /* ── highlights + what changed (10-26 call). Editor: their own highlights for the next
     round. Division: while their round is open, the highlights saved with the version they
     see, plus every block that changed since their last round, its new words marked. ── */
  const changes = useRoundChanges(p, blocks);
  const lastV = s.versions.length ? s.versions[s.versions.length - 1] : null;
  const hlShown: ProofHighlight[] = p.mode === "edit" ? p.hl : !s.locked && ui.showChanges && lastV && lastV.highlights ? lastV.highlights : [];
  const chgShown = p.mode === "comment" && ui.showChanges && changes ? changes.items : [];
  const [marks, setMarks] = useState<Array<{ x: number; y: number; w: number; h: number; k: "hl" | "chg"; t?: string; hi?: number }>>([]);
  useLayoutEffect(() => {
    const root = paperRef.current;
    if (!root) { setMarks([]); return; }
    const out: typeof marks = [];
    const z = zoom || 1;
    const r0 = root.getBoundingClientRect();
    const push = (rs: DOMRect[], k: "hl" | "chg", t?: string, hi?: number) => rs.forEach((r) => {
      if (r.width < 1 || r.height < 1) return;
      out.push({ x: (r.left - r0.left) / z, y: (r.top - r0.top) / z, w: r.width / z, h: r.height / z, k, t, hi });
    });
    hlShown.forEach((h, hi) => {
      if (!h.text) return;
      const b = resolveBlock(blocks, h.block);
      const el = b ? blockEl(root, b.id) : null;
      if (el) push(phraseRects(el, h.text), "hl", h.note, hi);
    });
    for (const c of chgShown) {
      const el = blockEl(root, c.id);
      // the new words, found on the page; a phrase the page doesn't show leaves just the outline
      if (el) for (const ph of c.add) push(phraseRects(el, ph), "chg");
    }
    // only when something moved — an identical list must not re-render (and re-run this)
    setMarks((cur) => (JSON.stringify(cur) === JSON.stringify(out) ? cur : out));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, blocks, zoom, paperRef, JSON.stringify(hlShown), changes, ui.showChanges, box]);
  const pos = (key?: string, x = 0, y = 0) => {
    const b = resolveBlock(blocks, key);
    const r = b && box[b.id];
    return r ? { x: r.x + x * r.w, y: r.y + y * r.h, r } : null;
  };
  const canUnHl = p.mode === "edit" && !!p.data.can?.save;
  const unHl = (i: number) => p.setHl(p.hl.filter((_, j) => j !== i));
  const vis = s.comments.filter((c) => ui.rf === "all" || c.round === ui.rf);
  const f = ui.focus ? s.comments.find((c) => c.id === ui.focus) : null;
  const fp = f ? pos(f.anchor.block, f.anchor.x, f.anchor.y) : null;
  const dp = ui.draft ? pos(ui.draft.block, ui.draft.x, ui.draft.y) : null;
  return (
    <div className="ppins">
      {hlShown.map((h, hi) => {
        if (h.text) return null;
        const b = resolveBlock(blocks, h.block);
        const r = b && box[b.id];
        return r ? (
          <span key={"hb" + hi} className="phlbox" style={{ left: r.x, top: r.y, width: r.w, height: r.h }}>
            {/* Jack, 10-26: un-highlight right on the email */}
            {canUnHl && <button type="button" className="phlx" title="Remove highlight" aria-label="Remove highlight" onClick={(e) => { e.stopPropagation(); unHl(hi); }}>×</button>}
          </span>
        ) : null;
      })}
      {chgShown.map((c) => {
        const r = box[c.id];
        return r ? <span key={"cb" + c.id} className="pchgbox" style={{ left: r.x, top: r.y, width: r.w, height: r.h }}><em>Changed</em></span> : null;
      })}
      {marks.map((m, i) => {
        const lastOfHl = m.k === "hl" && m.hi != null && !marks.slice(i + 1).some((x) => x.hi === m.hi);
        return (
          <span key={"m" + i} className={m.k === "hl" ? "phlmark" : "pchgmark"} style={{ left: m.x, top: m.y, width: m.w, height: m.h }}>
            {lastOfHl && canUnHl && <button type="button" className="phlx sm" title="Remove highlight" aria-label="Remove highlight" onClick={(e) => { e.stopPropagation(); unHl(m.hi as number); }}>×</button>}
          </span>
        );
      })}
      {f && fp && <span className="hlbox" style={{ ["--c" as string]: roundColor(f.round), left: fp.r.x, top: fp.r.y, width: fp.r.w, height: fp.r.h }}><em>{blockName(blocks, f.anchor.block)}</em></span>}
      {vis.map((c) => {
        const q = pos(c.anchor.block, c.anchor.x, c.anchor.y);
        if (!q) return null;
        const res = c.state === "resolved";
        return (
          <button key={c.id} type="button" className={"pin" + (res ? " res" : "") + (ui.focus === c.id ? " on" : "")}
            style={{ ["--c" as string]: roundColor(c.round), left: q.x, top: q.y }}
            title={(c.round ? "Round " + c.round : "Before round 1") + " · " + blockName(blocks, c.anchor.block)}
            onClick={(e) => { e.stopPropagation(); ui.setDraft(null); ui.setFocus(ui.focus === c.id ? null : c.id); if (ui.rf !== "all" && c.round !== ui.rf) ui.setRf("all"); }}>
            {res ? "✓" : c.n}
          </button>
        );
      })}
      {ui.draft && dp && (
        <>
          <span className="hlbox" style={{ ["--c" as string]: roundColor(s.round), left: dp.r.x, top: dp.r.y, width: dp.r.w, height: dp.r.h }}><em>{blockName(blocks, ui.draft.block)}</em></span>
          <span className="pin draft" style={{ ["--c" as string]: roundColor(s.round), left: dp.x, top: dp.y }}>{s.comments.length + 1}</span>
        </>
      )}
    </div>
  );
}

/* ── one comment card (mockup .cm2) ── */
function Card({ c, p, ui, blocks, side }: { c: ProofComment; p: ProofCtx; ui: ProofUi; blocks: AnyBlockInstance[]; side: "comment" | "edit" }) {
  const s = p.data.state!, can = p.data.can!, me = p.data.me!;
  const on = ui.focus === c.id, res = c.state === "resolved";
  const [reply, setReply] = useState<string | null>(null);
  const [edit, setEdit] = useState<string | null>(null);
  const run = async (op: ProofOp, body: Record<string, unknown>, after?: () => void) => {
    ui.setErr("");
    const e = await p.act(op, { cid: c.id, ...body });
    if (e) ui.setErr(e); else after?.();
  };
  const mine = c.author.email.toLowerCase() === me.email.toLowerCase();
  const canChange = !s.approved && c.replies.length === 0 && (me.role === "admin" || (mine && (me.role !== "reviewer" || !s.locked)));
  return (
    <div className={"cm2" + (on ? " on" : "") + (res ? " res" : "")} data-card={c.id} style={{ ["--c" as string]: roundColor(c.round) }}
      onClick={(e) => { if ((e.target as HTMLElement).closest("button,textarea,input")) return; ui.setDraft(null); ui.setFocus(on ? null : c.id); }}>
      <div className="r1"><span className="pn">{res ? "✓" : c.n}</span><b>{blockName(blocks, c.anchor.block)}</b><span className={"pl " + (res ? "f" : "o")}>{res ? "Fixed" : "Open"}</span></div>
      {edit !== null ? (
        <div className="pbox">
          <textarea className="fta" rows={3} maxLength={2000} autoFocus value={edit} onChange={(e) => setEdit(e.target.value)} />
          <div className="rowb"><button className="btn" disabled={p.busy || !edit.trim()} onClick={() => run("edit", { text: edit }, () => setEdit(null))}>Save</button><button className="btn ghost" onClick={() => setEdit(null)}>Cancel</button></div>
        </div>
      ) : <div className="tx">{c.text}</div>}
      <div className="by"><span className="rtag">{c.round ? "Round " + c.round : "Pre-review"}</span>{c.tag} · {c.author.name} · {when(c.createdAt)}{!on && c.replies.length ? " · " + c.replies.length + (c.replies.length > 1 ? " replies" : " reply") : ""}</div>
      {on && (
        <>
          {c.replies.length > 0 && <div className="rp">{c.replies.map((r) => <div key={r.id} className="rp1"><b>{r.author.name}</b> <span>{when(r.createdAt)}</span><div>{r.text}</div></div>)}</div>}
          {reply !== null && (
            <div className="pbox">
              <textarea className="fta" rows={2} maxLength={2000} autoFocus placeholder="Reply…" value={reply} onChange={(e) => setReply(e.target.value)} />
              <div className="rowb">
                <button className="btn" disabled={p.busy || !reply.trim()} onClick={() => run("reply", { text: reply }, () => { setReply(null); if (side === "comment") ui.setNotice("Reply saved. When you're done, change the job status in Airtable so Digital Ops knows."); })}>Post reply</button>
                <button className="btn ghost" onClick={() => setReply(null)}>Cancel</button>
              </div>
            </div>
          )}
          <div className="act">
            {can.comment && reply === null && <button onClick={() => setReply("")}>Reply</button>}
            {side === "edit" && can.resolve && (res
              ? <button onClick={() => run("resolve", { resolved: false })}>Reopen</button>
              : <button onClick={() => run("resolve", { resolved: true })}>Mark resolved</button>)}
            {canChange && edit === null && <button onClick={() => setEdit(c.text)}>Edit</button>}
            {canChange && <button onClick={() => run("delete", {}, () => ui.setFocus(null))}>Delete</button>}
          </div>
        </>
      )}
    </div>
  );
}

function RoundChips({ p, ui }: { p: ProofCtx; ui: ProofUi }) {
  const s = p.data.state!;
  const rounds = Array.from(new Set(s.comments.map((c) => c.round))).sort((a, b) => a - b);
  if (rounds.length < 2) return null;
  return (
    <div className="rchips">
      {rounds.map((r) => <button key={r} className={"rch" + (ui.rf === r ? " on" : "")} style={{ ["--c" as string]: roundColor(r) }} onClick={() => { ui.setRf(r); ui.setFocus(null); }}><i />{r ? "R" + r : "Pre"}</button>)}
      <button className={"rch" + (ui.rf === "all" ? " on" : "")} style={{ ["--c" as string]: "#9fb0cc" }} onClick={() => { ui.setRf("all"); ui.setFocus(null); }}><i />All</button>
    </div>
  );
}

function List({ p, ui, blocks, side, only }: { p: ProofCtx; ui: ProofUi; blocks: AnyBlockInstance[]; side: "comment" | "edit"; only?: string }) {
  const s = p.data.state!;
  let L = s.comments.filter((c) => ui.rf === "all" || c.round === ui.rf);
  if (only) L = L.filter((c) => resolveBlock(blocks, c.anchor.block)?.id === only);
  L = L.slice().sort((a, b) => (a.state === "open" ? 0 : 1) - (b.state === "open" ? 0 : 1) || b.round - a.round || a.n - b.n);
  if (!L.length) return <div className="ihint" style={{ padding: "14px 0" }}>No comments {only ? "on this block" : ui.rf === "all" ? "yet" : "in this round"}.</div>;
  return <div className="list">{L.map((c) => <Card key={c.id} c={c} p={p} ui={ui} blocks={blocks} side={side} />)}</div>;
}

function Composer({ p, ui, blocks }: { p: ProofCtx; ui: ProofUi; blocks: AnyBlockInstance[] }) {
  const s = p.data.state!;
  const [tag, setTag] = useState("Copy");
  const [text, setText] = useState("");
  const d = ui.draft!;
  const latest = s.versions.length ? s.versions[s.versions.length - 1].n : undefined;
  const post = async () => {
    if (!text.trim()) return;
    ui.setErr("");
    const e = await p.act("comment", { text, tag, anchor: { block: d.block, x: d.x, y: d.y }, ...(latest ? { version: latest } : {}) });
    if (e) { ui.setErr(e); return; }
    ui.setDraft(null);
    if (p.mode === "comment") ui.setNotice("Comment posted. When you're done, change the job status in Airtable so Digital Ops knows.");
  };
  return (
    <div className="cmp" style={{ ["--c" as string]: roundColor(s.round) }}>
      <div className="cmph"><span className="pn">{s.comments.length + 1}</span><b>New comment · {blockName(blocks, d.block)}</b><span className="rtag">{s.round ? "Round " + s.round : "Pre-review"}</span></div>
      {/* the app's own dropdown (Combo, as on the request form) — Jack, 2026-10-07 */}
      <div className="ptagl">Type
        <Combo className="ptag" value={tag} onChange={setTag} title="What's it about" options={PROOF_TAGS.map((t) => ({ v: t }))} />
      </div>
      <textarea className="fta" id="pcText" autoFocus maxLength={2000} placeholder="What needs to change here?" style={{ minHeight: 70 }} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="rowb"><button className="btn" disabled={p.busy || !text.trim()} onClick={post}>Post comment</button><button className="btn ghost" onClick={() => ui.setDraft(null)}>Cancel</button></div>
    </div>
  );
}

/* ── VERSION HISTORY, IN THE LEFT PANEL (M2 Discovery, 2026-10-12) ─────────────────
   Uche: "version control so we can see what previous versions look like … what changes were
   requested, what comments were made, what did we do." Each saved version is the exact email
   as it was saved (proofing/<id>/versions in AWS, never Marketing Cloud — Justin: two emails
   with the same name would confuse the send team). View opens one read-only on the canvas;
   ticking two puts them side by side (the "side-by-side compare" wish from the same call). */
/* Katy, 10-26 call: "I don't know if we really need the version in there". A saved version is
   named by the round that showed it to the division (R2); one that hasn't gone out yet is a
   Draft. The v-number stays underneath as the key — it is just never shown. */
export function roundOfVersion(s: ProofState, n: number): number | null {
  // the FIRST round that showed it (a version re-sent in a later round keeps its name)
  const r = (s.rounds || []).find((x) => x.base === n);
  return r ? r.round : null;
}
export const vShort = (s: ProofState, n: number) => { const r = roundOfVersion(s, n); return r ? "R" + r : "Draft"; };
export const vName = (s: ProofState, n: number) => { const r = roundOfVersion(s, n); return r ? "Round " + r : "Draft"; };
function VersionList({ p, ui }: { p: ProofCtx; ui: ProofUi }) {
  const s = p.data.state!;
  const latest = s.versions.length ? s.versions[s.versions.length - 1].n : 0;
  const picked = ui.cmp ? ui.cmp.slice() : [];
  const toggle = (n: number) => {
    let next = picked.includes(n) ? picked.filter((x) => x !== n) : picked.concat(n);
    if (next.length > 2) next = next.slice(-2);
    if (next.length === 2) { ui.setView(null); ui.setCmp([Math.min(next[0], next[1]), Math.max(next[0], next[1])]); }
    else { ui.setCmp(null); if (next.length === 1) ui.setView(next[0]); }
  };
  if (!s.versions.length) return <div className="ihint" style={{ padding: "14px 0" }}>No saved versions yet.</div>;
  return (
    <div className="list pvlist">
      {s.versions.slice().reverse().map((v) => {
        const nC = s.comments.filter((c) => c.version === v.n).length;
        const r = roundOfVersion(s, v.n);
        const viewing = ui.view === v.n || (!ui.view && !ui.cmp && v.n === latest);
        return (
          <div key={v.n} className={"cm2 pver" + (viewing ? " on" : "")} style={{ ["--c" as string]: v.n === latest ? "#1B2A58" : roundColor(r || 1) }}>
            <div className="r1"><span className="pn" style={{ background: r ? roundColor(r) : "#9fb0cc" }}>{vShort(s, v.n)}</span><b>{v.note || "Saved"}</b>{v.n === latest && <span className="pl f">Current</span>}</div>
            <div className="by">{v.by.name} · {when(v.t)}{nC ? " · " + nC + " comment" + (nC === 1 ? "" : "s") : ""}</div>
            <div className="pvact">
              <button className="pvb" onClick={() => { ui.setCmp(null); ui.setView(v.n === latest ? null : v.n); }}>{viewing ? "Viewing" : "View"}</button>
              <label><input type="checkbox" checked={picked.includes(v.n)} onChange={() => toggle(v.n)} /> Compare</label>
            </div>
          </div>
        );
      })}
      <div className="pvfoot">Tick two to see them side by side. Each version is the email exactly as it was saved.</div>
    </div>
  );
}

function LeftTabs({ p, ui }: { p: ProofCtx; ui: ProofUi }) {
  const s = p.data.state!;
  const open = s.comments.filter((c) => c.state === "open").length;
  return (
    <div className="ptabs" role="tablist">
      <button role="tab" className={ui.ltab === "comments" ? "on" : ""} onClick={() => ui.setLtab("comments")}>Comments<span>{open}</span></button>
      <button role="tab" className={ui.ltab === "versions" ? "on" : ""} onClick={() => ui.setLtab("versions")}>Versions<span>{s.versions.length}</span></button>
    </div>
  );
}

/* ── Edit | Compare in the top bar: Compare shows the last two saved versions side by side ── */
export function ProofCompareSeg({ p, ui }: { p: ProofCtx; ui: ProofUi }) {
  const vs = p.data.state!.versions;
  if (vs.length < 2) return null;
  const on = !!ui.cmp;
  const last2 = (): [number, number] => [vs[vs.length - 2].n, vs[vs.length - 1].n];
  return (
    <div className="esdev pcmpseg">
      <button className={!on ? "on" : ""} onClick={() => { ui.setCmp(null); ui.setView(null); }}>{p.mode === "edit" ? "Edit" : "Review"}</button>
      <button className={on ? "on" : ""} onClick={() => { ui.setView(null); ui.setCmp(ui.cmp || last2()); ui.setLtab("versions"); }}>Compare</button>
    </div>
  );
}

/* ── the canvas when a saved version is open: one read-only paper, or two side by side ── */
/* ── B: THE PICKER ON EACH SIDE (Jack chose it, 2026-10-12). Each side's label is the
   control: "v2 · Tue 7:57 PM ▾" opens the saved versions and swaps that side in place. An
   in-page panel in the app's own look (never a native select — standing rule). The version
   already on the other side is shown but can't be picked, so the two sides never match. */
function VersionPicker({ p, n, other, onPick, label }: { p: ProofCtx; n: number; other: number; onPick: (n: number) => void; label: string }) {
  const s = p.data.state!;
  const latest = s.versions.length ? s.versions[s.versions.length - 1].n : 0;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("click", close); document.removeEventListener("keydown", key); };
  }, [open]);
  return (
    <span className="pvpick">
      <button type="button" className={"pvpickb" + (open ? " on" : "")} aria-haspopup="listbox" aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>{label} <span className="cv">&#9662;</span></button>
      {open && (
        <span className="pvmenu" role="listbox" onClick={(e) => e.stopPropagation()}>
          {s.versions.slice().reverse().map((v) => {
            const taken = v.n === other;
            return (
              <button type="button" key={v.n} role="option" aria-selected={v.n === n} disabled={taken}
                className={(v.n === n ? "on" : "") + (taken ? " off" : "")}
                title={taken ? "Already on the other side" : undefined}
                onClick={() => { setOpen(false); if (!taken && v.n !== n) onPick(v.n); }}>
                <b>{vName(s, v.n)}{v.n === latest ? " · current" : ""}</b>
                <span>{v.note || "Saved"} · {when(v.t)}</span>
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
}

function VersionPaper({ id, n, label, scale, head }: { id: string; n: number; label: string; scale: number; head?: ReactNode }) {
  const [html, setHtml] = useState<string | null>(null);
  const [h, setH] = useState(900);
  useEffect(() => {
    let live = true;
    setHtml(null);
    getProofVersionHtml(id, n).then((t) => { if (live) setHtml(t || ""); });
    return () => { live = false; };
  }, [id, n]);
  return (
    <div className="pvcol">
      <div className="pvlab">{head || label}</div>
      <div className="pvbox" style={{ width: 600 * scale, height: h * scale }}>
        {html === null ? <div className="pvload">Loading…</div> : !html ? <div className="pvload">This version couldn't be loaded.</div> : (
          <iframe title={label} className="pvframe" sandbox="allow-same-origin" scrolling="no" srcDoc={html} style={{ width: 600, height: h, transform: "scale(" + scale + ")" }}
            onLoad={(e) => {
              /* the paper is as tall as the email: measured on load, and again as each
                 image arrives (a photo landing after load made the frame scroll) */
              const f = e.currentTarget as HTMLIFrameElement;
              const fit = () => { try { const d = f.contentDocument; if (d && d.body) setH(Math.max(400, d.documentElement.scrollHeight, d.body.scrollHeight)); } catch { /* srcDoc is same-origin */ } };
              fit();
              try { f.contentDocument?.querySelectorAll("img").forEach((im) => im.addEventListener("load", fit)); } catch { /* same */ }
              window.setTimeout(fit, 800);
            }} />
        )}
      </div>
    </div>
  );
}
export function ProofVersionCanvas({ p, ui }: { p: ProofCtx; ui: ProofUi }) {
  const s = p.data.state!;
  const latest = s.versions.length ? s.versions[s.versions.length - 1].n : 0;
  const lab = (n: number) => {
    const v = s.versions.find((x) => x.n === n);
    return vName(s, n) + (n === latest ? " · current" : "") + (v ? " · " + when(v.t) : "");
  };
  if (ui.cmp) {
    const [a, b] = ui.cmp;
    return (
      <div className="pvwrap">
        <div className="pvbanner">Comparing {lab(a)} and {lab(b)} · read-only <button className="estb" onClick={() => ui.setCmp(null)}>Back to {p.mode === "edit" ? "editing" : "the email"}</button></div>
        <div className="pvrow">
          <VersionPaper id={p.id} n={a} label={lab(a)} scale={0.8} head={<VersionPicker p={p} n={a} other={b} label={lab(a)} onPick={(x) => ui.setCmp([x, b])} />} />
          <VersionPaper id={p.id} n={b} label={lab(b)} scale={0.8} head={<VersionPicker p={p} n={b} other={a} label={lab(b)} onPick={(x) => ui.setCmp([a, x])} />} />
        </div>
      </div>
    );
  }
  if (ui.view) {
    return (
      <div className="pvwrap">
        <div className="pvbanner">Viewing {lab(ui.view)} · read-only <button className="estb" onClick={() => ui.setView(null)}>Back to current</button></div>
        <div className="pvrow"><VersionPaper id={p.id} n={ui.view} label={lab(ui.view)} scale={1} /></div>
      </div>
    );
  }
  return null;
}

/* ── the inspector in proof mode. `fields` is the Studio's own block Inspector, so an
   editor edits exactly the way they do in Design Email today. ── */
export function ProofInspector({ p, ui, blocks, sel, onDeselect, fields }: {
  p: ProofCtx; ui: ProofUi; blocks: AnyBlockInstance[]; sel: AnyBlockInstance | null; onDeselect: () => void; fields: ReactNode;
}) {
  const s = p.data.state!, can = p.data.can!;
  const open = s.comments.filter((c) => c.state === "open").length;
  if (p.mode === "comment") {
    return (
      <>
        <div className="ih"><span className="ihdot" style={{ background: roundColor(s.round) }} /><span className="ihtx">Comments<small>{!s.locked ? "Round " + s.round + " open" : s.round ? "Round " + s.round + " closed" : "Not open yet"} · {open} open</small></span></div>
        <LeftTabs p={p} ui={ui} />
        <div className="ib">
          <ProofSide p={p} ui={ui} />
          {ui.ltab === "versions" ? <VersionList p={p} ui={ui} /> : <>
            <WhatsNew p={p} ui={ui} blocks={blocks} />
            <RoundChips p={p} ui={ui} />
            {ui.draft && can.comment && <Composer p={p} ui={ui} blocks={blocks} />}
            <List p={p} ui={ui} blocks={blocks} side="comment" />
          </>}
        </div>
      </>
    );
  }
  if (sel) {
    const n = s.comments.filter((c) => resolveBlock(blocks, c.anchor.block)?.id === sel.id).length;
    return (
      <>
        <div className="ih"><span className="ihdot" /><span className="ihtx">{blockLabel(sel.type)}<small>Block</small></span><button className="ihx" aria-label="Close" onClick={onDeselect}>×</button></div>
        <div className="ib">
          <ProofSide p={p} ui={ui} />
          <div className="iseg"><button className={ui.itab === "fields" ? "on" : ""} onClick={() => ui.setItab("fields")}>Fields</button><button className={ui.itab === "comments" ? "on" : ""} onClick={() => ui.setItab("comments")}>Comments{n ? " (" + n + ")" : ""}</button></div>
          {ui.itab === "fields" ? (can.editContent ? fields : <div className="ihint">This email is approved. Nothing can be edited.</div>) : (
            <>
              {ui.draft ? <Composer p={p} ui={ui} blocks={blocks} /> : null}
              <List p={p} ui={ui} blocks={blocks} side="edit" only={sel.id} />
            </>
          )}
          <button className="backl" onClick={onDeselect}>← All comments</button>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="ih"><span className="ihdot" /><span className="ihtx">Comments<small>{!s.locked ? "Round " + s.round + " open" : s.round ? "Round " + s.round + " closed" : "Not open yet"} · {open} open</small></span>
        {can.comment && !ui.draft && <button className={"estb padd" + (ui.adding ? " on" : "")} onClick={() => { ui.setFocus(null); ui.setAdding(!ui.adding); }}>{ui.adding ? "Cancel" : "+ Add comment"}</button>}
      </div>
      <LeftTabs p={p} ui={ui} />
      <div className="ib">
        <ProofSide p={p} ui={ui} />
        {ui.ltab === "versions" ? <VersionList p={p} ui={ui} /> : <>
          <RoundChips p={p} ui={ui} />
          {ui.draft ? <Composer p={p} ui={ui} blocks={blocks} /> : null}
          <List p={p} ui={ui} blocks={blocks} side="edit" />
        </>}
      </div>
    </>
  );
}

/* where a click on a block lands, as a fraction of that block */
export function dropAt(e: React.MouseEvent<HTMLElement>, blocks: AnyBlockInstance[], id: string) {
  const r = e.currentTarget.getBoundingClientRect();
  return {
    block: blockKey(blocks, id),
    x: Math.max(0, Math.min(1, (e.clientX - r.left) / (r.width || 1))),
    y: Math.max(0, Math.min(1, (e.clientY - r.top) / (r.height || 1))),
  };
}

export const openOnBlock = (p: ProofCtx | undefined, blockId: string, blocks: AnyBlockInstance[]) =>
  p ? p.data.state!.comments.filter((c) => c.state === "open" && resolveBlock(blocks, c.anchor.block)?.id === blockId).length : 0;

/* ── ROUNDS + HIGHLIGHTS (10-26 proofing call: Katy, Katie McCarthy, Lauren, Jack) ─────────
   1. Digital Ops picks the round number so the hub matches the Airtable job.
   2. A saved version is named by its round (R2), never "v3".
   3. Digital Ops can highlight a block or words for the division, with a note.
   4. The division sees what changed since their last round, worked out from the saved
      versions — nobody marks changes by hand.
   The server keeps the round labels (proof-model `unlock {round}` / `setround`) and stores the
   highlights on the version (`proof-save {highlights}`). */

/* the round picker: the app's own pick panel, never a native select (standing rule) */
function RoundPick({ title, foot, rows, on, onPick, onClose }: {
  title: string; foot: string; rows: Array<{ n: number; note?: string }>; on: number | null; onPick: (n: number) => void; onClose: () => void;
}) {
  const [hi, setHi] = useState(Math.max(0, rows.findIndex((r) => r.n === on)));
  useEffect(() => {
    const close = () => onClose();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowDown") { e.preventDefault(); setHi((i) => Math.min(rows.length - 1, i + 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setHi((i) => Math.max(0, i - 1)); }
      else if (e.key === "Enter" && rows[hi]) { e.preventDefault(); onPick(rows[hi].n); }
    };
    /* listen from the NEXT tick: the click that opened the panel must not also close it
       (React flushes this effect before that click finishes bubbling) */
    const t = window.setTimeout(() => document.addEventListener("click", close), 0);
    document.addEventListener("keydown", key);
    return () => { window.clearTimeout(t); document.removeEventListener("click", close); document.removeEventListener("keydown", key); };
  }, [rows, hi, onPick, onClose]);
  return (
    <span className="rpick" role="listbox" aria-label={title} onClick={(e) => e.stopPropagation()}>
      <span className="h">{title}</span>
      {rows.map((r, i) => (
        <button type="button" key={r.n} role="option" aria-selected={r.n === on} className={"row" + (i === hi ? " on" : "")}
          style={{ ["--c" as string]: roundColor(r.n) }} onMouseEnter={() => setHi(i)} onClick={() => onPick(r.n)}>
          <i />Round {r.n}{r.note ? <small>{r.note}</small> : null}
        </button>
      ))}
      <span className="foot">{foot}</span>
    </span>
  );
}

export function roundRows(s: ProofState, mode: "open" | "relabel"): Array<{ n: number; note?: string }> {
  const top = s.round + 1;
  const out: Array<{ n: number; note?: string }> = [];
  if (mode === "open") {
    out.push({ n: top, note: "next" });
    if (s.round >= 1) out.push({ n: s.round, note: "reopen" });
    for (let n = s.round - 1; n >= 1; n--) out.push({ n });
  } else {
    for (let n = top; n >= 1; n--) if (n !== s.round) out.push({ n });
  }
  return out;
}

/* ── highlights (editor) ── */
export function toggleBlockHl(p: ProofCtx, blocks: AnyBlockInstance[], id: string) {
  const i = p.hl.findIndex((h) => !h.text && resolveBlock(blocks, h.block)?.id === id);
  p.setHl(i >= 0 ? p.hl.filter((_, j) => j !== i) : p.hl.concat({ block: blockKey(blocks, id) }));
}
/** highlight the words selected in the email; returns an error line, or "" */
export function addTextHighlight(p: ProofCtx, blocks: AnyBlockInstance[]): string {
  const sel = typeof window !== "undefined" ? window.getSelection() : null;
  const text = sel ? String(sel).replace(/\s+/g, " ").trim() : "";
  const node = sel && sel.anchorNode;
  const host = node ? (node.nodeType === 1 ? (node as Element) : node.parentElement) : null;
  const mod = host && host.closest ? (host.closest(".esmod[data-id]") as HTMLElement | null) : null;
  if (!text || !mod) return "Select some words in the email first, then click Highlight text.";
  const key = blockKey(blocks, mod.dataset.id || "");
  if (!key) return "Select some words in the email first, then click Highlight text.";
  if (!p.hl.some((h) => h.block === key && h.text === text.slice(0, 300))) p.setHl(p.hl.concat({ block: key, text: text.slice(0, 300) }));
  try { sel && sel.removeAllRanges(); } catch { /* old browser */ }
  return "";
}

/* ── finding words on the rendered email (no change to the email itself: marks are drawn
   over it, like the pins) ── */
function blockEl(root: HTMLElement, id: string): HTMLElement | null {
  const m = root.querySelector<HTMLElement>('.esmod[data-id="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
  return m ? (m.querySelector<HTMLElement>(".mi") || m) : null;
}
interface TMap { norm: string; at: Array<{ n: Text; o: number }> }
/** the block's visible text with spaces collapsed, and where each character sits */
function textMap(el: HTMLElement): TMap {
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => ((n.parentElement && n.parentElement.closest(".mtag,.mbar,style,script")) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  let norm = "", space = true;
  const at: TMap["at"] = [];
  for (let n = w.nextNode() as Text | null; n; n = w.nextNode() as Text | null) {
    const t = n.data;
    for (let i = 0; i < t.length; i++) {
      if (/\s/.test(t[i])) { if (!space) { norm += " "; at.push({ n, o: i }); space = true; } }
      else { norm += t[i]; at.push({ n, o: i }); space = false; }
    }
  }
  return { norm, at };
}
function rectsFor(m: TMap, from: number, to: number): DOMRect[] {
  if (from >= to || !m.at[from] || !m.at[to - 1]) return [];
  try {
    const r = document.createRange();
    r.setStart(m.at[from].n, m.at[from].o);
    r.setEnd(m.at[to - 1].n, m.at[to - 1].o + 1);
    return Array.from(r.getClientRects());
  } catch { return []; }
}
function phraseRects(el: HTMLElement, phrase: string): DOMRect[] {
  const m = textMap(el);
  const want = phrase.replace(/\s+/g, " ").trim();
  const i = want ? m.norm.indexOf(want) : -1;
  return i < 0 ? [] : rectsFor(m, i, i + want.length);
}
/** the words in the block now that weren't there before (longest-common-subsequence on words) */
export function insertedWords(oldText: string, newText: string): Array<[number, number]> {
  const a = oldText.split(" ").filter(Boolean);
  const toks: Array<{ w: string; s: number; e: number }> = [];
  newText.replace(/\S+/g, (w, s: number) => { toks.push({ w, s, e: s + w.length }); return w; });
  const b = toks.map((t) => t.w);
  const m = a.length, n = b.length;
  if (m * n > 250000) return toks.length ? [[0, newText.length]] : []; // a very long block: mark it all
  const L: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out: Array<[number, number]> = [];
  let i = 0, j = 0;
  while (j < n) {
    if (i < m && a[i] === b[j]) { i++; j++; }
    else if (i < m && L[i + 1][j] >= L[i][j + 1]) i++;
    else {
      const last = out[out.length - 1];
      if (last && j > 0 && last[1] === toks[j - 1].e) last[1] = toks[j].e; else out.push([toks[j].s, toks[j].e]);
      j++;
    }
  }
  return out;
}

/** a saved block's words, from its data (SFMC blocks carry their HTML; ECH blocks their fields) */
export function blockText(b: { data?: unknown } | null | undefined): string {
  const d = (b && b.data) || {};
  /* the words a reader sees: no styles, no HTML comments (SFMC blocks carry build notes in
     them), no {{field}} tokens, no AMPscript */
  /* The HTML is read by the browser's own parser, not by regexes (CodeQL flagged the regex
     tag-and-entity stripping on #407): style/script nodes are dropped, comments are never text,
     entities decode once, and each text node is a separate run so block edges stay word breaks. */
  const strip = (h: string): string => {
    const s = h.replace(/%%\[[\s\S]*?\]%%/g, " ").replace(/%%=[\s\S]*?=%%/g, " ").replace(/\{\{[^}]*\}\}/g, " ");
    if (s.indexOf("<") < 0 && s.indexOf("&") < 0) return s;
    const doc = new DOMParser().parseFromString("<!doctype html><body>" + s + "</body>", "text/html");
    doc.querySelectorAll("style,script").forEach((n) => n.remove());
    const runs: string[] = [];
    const tw = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    for (let n = tw.nextNode(); n; n = tw.nextNode()) runs.push(n.nodeValue || "");
    return runs.join(" ").replace(/ /g, " ").replace(/[‘’]/g, "'");
  };
  const parts: string[] = [];
  const walk = (v: unknown, k: string) => {
    if (typeof v === "string") {
      if (k.startsWith("__") && k !== "__html" && k !== "__dsText") return;
      if (/^(https?:|data:|#[0-9a-f]{3,8}$|mailto:|tel:)/i.test(v.trim())) return;
      parts.push(strip(v));
    } else if (Array.isArray(v)) v.forEach((x) => walk(x, k));
    else if (v && typeof v === "object") Object.entries(v as Record<string, unknown>).forEach(([kk, x]) => walk(x, kk));
  };
  walk(d, "");
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

/* the previous round's saved document, fetched once per version */
const prevDocs = new Map<string, Promise<AnyBlockInstance[] | null>>();
function prevBlocksOf(id: string, n: number): Promise<AnyBlockInstance[] | null> {
  const k = id + ":" + n;
  if (!prevDocs.has(k)) {
    prevDocs.set(k, getProofVersionDoc(id, n).then((t) => {
      try { const d = JSON.parse(t); return Array.isArray(d.blocks) ? (d.blocks as AnyBlockInstance[]) : null; } catch { return null; }
    }).catch(() => null));
  }
  return prevDocs.get(k)!;
}
export interface RoundChange { id: string; name: string; old: string; add: string[] }
/** the runs of words in `newT` that `oldT` didn't have, as text (for finding them on the page) */
export function insertedPhrases(oldT: string, newT: string): string[] {
  return insertedWords(oldT, newT).map(([a, z]) => newT.slice(a, z)).filter((x) => x.trim());
}
/**
 * Division view, round open: every block that differs from the version their last round showed.
 * BOTH sides are the SAVED documents (the previous round's version and the latest one), never
 * the live canvas: the Studio re-mints ids and re-fills fields when it opens an email, so the
 * canvas never equals any saved document (first local drive, 10-26: every block read "new").
 * A change is then shown on the canvas block it resolves to (index:type:id, as pins do).
 */
export function useRoundChanges(p: ProofCtx, blocks: AnyBlockInstance[]): { prev: number; items: RoundChange[] } | null {
  const s = p.data.state!;
  const rounds = s.rounds || [];
  const cur = rounds[rounds.length - 1], prev = rounds[rounds.length - 2];
  const latestN = s.versions.length ? s.versions[s.versions.length - 1].n : null;
  const prevBase = prev ? prev.base : null;
  const on = p.mode === "comment" && !s.locked && !!cur && prevBase != null && latestN != null && latestN !== prevBase;
  const [docs, setDocs] = useState<{ a: AnyBlockInstance[]; b: AnyBlockInstance[] } | null>(null);
  useEffect(() => {
    if (!on || prevBase == null || latestN == null) { setDocs(null); return; }
    let live = true;
    Promise.all([prevBlocksOf(p.id, prevBase), prevBlocksOf(p.id, latestN)]).then(([a, b]) => { if (live) setDocs(a && b ? { a, b } : null); });
    return () => { live = false; };
  }, [on, p.id, prevBase, latestN]);
  const prevRound = prev ? prev.round : 0;
  /* one object per real change — the pins' layout effect depends on it, so a fresh object
     on every render would loop */
  return useMemo(() => {
    if (!on || !docs) return null;
    const items: RoundChange[] = [];
    docs.b.forEach((nb, i) => {
      const o = docs.a.find((x) => x.id === nb.id) || (docs.a[i] && docs.a[i].type === nb.type ? docs.a[i] : undefined);
      if (o && JSON.stringify(o.data) === JSON.stringify(nb.data)) return;
      const shown = resolveBlock(blocks, i + ":" + nb.type + ":" + nb.id);
      if (!shown || items.some((x) => x.id === shown.id)) return;
      const oldT = o ? blockText(o) : "", newT = blockText(nb);
      if (o && oldT === newT && JSON.stringify(o.data).length === JSON.stringify(nb.data).length) return;
      items.push({ id: shown.id, name: blockLabel(shown.type), old: oldT, add: o ? insertedPhrases(oldT, newT) : [] });
    });
    return { prev: prevRound, items };
  }, [on, docs, blocks, prevRound]);
}

const jump = (id: string) => {
  const el = document.querySelector<HTMLElement>('.es.esproof .esmod[data-id="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
  if (!el) return;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  el.classList.remove("pflash"); void el.offsetWidth; el.classList.add("pflash");
};

/* division, round open: "What's new in round N" at the top of the comments */
function WhatsNew({ p, ui, blocks }: { p: ProofCtx; ui: ProofUi; blocks: AnyBlockInstance[] }) {
  const s = p.data.state!;
  const changes = useRoundChanges(p, blocks);
  const lastV = s.versions.length ? s.versions[s.versions.length - 1] : null;
  const hl = lastV && lastV.highlights ? lastV.highlights : [];
  if (s.locked || (!hl.length && !(changes && changes.items.length))) return null;
  return (
    <div className="pwn">
      <button type="button" className="pwnh" onClick={() => ui.setShowChanges(!ui.showChanges)} aria-pressed={ui.showChanges}>
        <span className={"tog" + (ui.showChanges ? "" : " off")} />What's new in round {s.round}
      </button>
      {/* highlights show on the email only (Jack, 10-26: "nothing else"); the switch hides them too */}
      {ui.showChanges && <>
        {changes && changes.items.length > 0 && <div className="sec">Changed since round {changes.prev}</div>}
        {changes && changes.items.map((c) => (
          <button type="button" key={c.id} className="it" onClick={() => jump(c.id)}>
            <b>{c.name}</b>{c.old ? <small>was: <s>{c.old.length > 140 ? c.old.slice(0, 140) + "…" : c.old}</s></small> : <small>new block</small>}
          </button>
        ))}
      </>}
    </div>
  );
}
